import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const fadeUp = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.24, ease: [0.2, 0.8, 0.2, 1] } }
};

/**
 * Sección "Borradores pendientes" del Dashboard.
 *
 * @param {Array} drafts      lista de borradores {_id, nombre, lugar_trabajo, correo_corporativo, created_at, token_expiration_hours}
 * @param {string|null} sendingDraft  id del draft que se está enviando (para loading)
 * @param {string|null} deletingDraft id del draft que se está borrando (para loading)
 * @param {function} onSendDraft      callback (draftId) => void
 * @param {function} onDeleteDraft    callback (draft) => void — abre el diálogo de borrado
 */
export function DraftsList({ drafts, sendingDraft, deletingDraft, onSendDraft, onDeleteDraft }) {
  if (!drafts || drafts.length === 0) return null;

  return (
    <motion.div variants={fadeUp} style={{ marginBottom: '24px' }}>
      <Card style={{ borderLeft: '4px solid #f59e0b' }}>
        <CardHeader>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <CardTitle
                style={{ fontSize: '18px', color: '#1a1a1a', display: 'flex', alignItems: 'center', gap: '8px' }}
                data-testid="drafts-list-title"
              >
                💾 Borradores Pendientes
                <Badge style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>
                  {drafts.length}
                </Badge>
              </CardTitle>
              <CardDescription style={{ marginTop: '4px' }}>
                Altas guardadas listas para enviar cuando el enrolamiento esté completo
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div style={{ display: 'grid', gap: '12px' }} data-testid="drafts-list">
            {drafts.map(draft => (
              <div
                key={draft.id}
                data-testid="draft-row"
                style={{
                  background: '#fffbeb',
                  border: '1px solid #fde68a',
                  borderRadius: '10px',
                  padding: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '16px'
                }}
              >
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
                    <h4 style={{ fontSize: '15px', fontWeight: 700, color: '#1a1a1a', margin: 0 }}>
                      {draft.nombre}
                    </h4>
                    <Badge style={{ background: '#fff', border: '1px solid #e5e7eb', color: '#666', fontSize: '11px' }}>
                      {draft.lugar_trabajo || 'Sin departamento'}
                    </Badge>
                  </div>
                  <div style={{ display: 'flex', gap: '12px', fontSize: '13px', color: '#666', flexWrap: 'wrap' }}>
                    <span>📧 {draft.correo_corporativo}</span>
                    <span>•</span>
                    <span>📅 Creado: {new Date(draft.created_at).toLocaleDateString('es-ES')}</span>
                    <span>•</span>
                    <span>⏱️ Expira en {draft.token_expiration_hours}h</span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <Button
                    variant="outline"
                    data-testid="draft-edit-button"
                    onClick={() => window.location.href = `/form?id=${draft.id}`}
                    style={{
                      height: '36px',
                      padding: '0 16px',
                      fontSize: '13px',
                      borderColor: '#d1d5db',
                      color: '#666'
                    }}
                  >
                    ✏️ Editar
                  </Button>
                  <Button
                    data-testid="draft-send-button"
                    onClick={() => onSendDraft(draft.id)}
                    disabled={sendingDraft === draft.id || deletingDraft === draft.id}
                    style={{
                      height: '36px',
                      padding: '0 16px',
                      fontSize: '13px',
                      background: sendingDraft === draft.id ? '#9ca3af' : '#1a5c2e',
                      color: '#fff'
                    }}
                  >
                    {sendingDraft === draft.id ? '⏳ Enviando...' : '📧 Enviar ahora'}
                  </Button>
                  <Button
                    variant="outline"
                    data-testid="draft-delete-button"
                    onClick={() => onDeleteDraft && onDeleteDraft(draft)}
                    disabled={sendingDraft === draft.id || deletingDraft === draft.id}
                    style={{
                      height: '36px',
                      padding: '0 14px',
                      fontSize: '13px',
                      borderColor: '#fecaca',
                      color: '#b91c1c',
                      background: '#fff'
                    }}
                    title="Borrar borrador (no se incorpora)"
                  >
                    {deletingDraft === draft.id ? '⏳' : '🗑️ Borrar'}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
}

export default DraftsList;
